import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiMoreVertical, FiEdit2, FiTrash2, FiCheckCircle, FiXCircle, FiPlay, FiServer, FiAlertCircle, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import { SiAmazon, SiGooglecloud } from 'react-icons/si';
import { useTranslation } from 'react-i18next';
import {
  ConnectionService,
  CreateConnectionData,
  getConnections,
  getConnectionWithCredentials,
  createConnection,
  updateConnection,
  deleteConnection,
  testConnection,
  toggleConnection,
} from '../../api/connections';

type Provider = 'azure' | 'aws' | 'gcp' | 'fabric' | 'purview';

const ConnectionsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const [connections, setConnections] = useState<ConnectionService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingConnection, setEditingConnection] = useState<ConnectionService | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getConnections();
      setConnections(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // Fermer le menu au clic extérieur
  useEffect(() => {
    if (!activeMenu) return;
    const handleClickOutside = () => setActiveMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeMenu]);

  const handleToggleConnection = async (id: string) => {
    setActiveMenu(null);
    try {
      await toggleConnection(id);
      setConnections(prev => prev.map(c => c.id === id ? { ...c, is_active: !c.is_active } : c));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to toggle connection');
    }
  };

  const handleDeleteConnection = async (id: string) => {
    if (!window.confirm(t('connections.deleteConfirm'))) return;
    setActiveMenu(null);
    try {
      await deleteConnection(id);
      setConnections(prev => prev.filter(c => c.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete connection');
    }
  };

  const handleTestConnection = async (id: string) => {
    setActiveMenu(null);
    setTestingId(id);
    try {
      const result = await testConnection(id);
      const testMessage = result.success ? result.message : (result.error || result.message);
      setConnections(prev => prev.map(c =>
        c.id === id
          ? { ...c, test_status: result.success ? 'success' : 'failed', last_tested_at: new Date().toISOString(), test_message: testMessage }
          : c
      ));
    } catch (err: any) {
      setConnections(prev => prev.map(c =>
        c.id === id ? { ...c, test_status: 'failed', last_tested_at: new Date().toISOString() } : c
      ));
    } finally {
      setTestingId(null);
    }
  };

  const handleSave = async (data: CreateConnectionData) => {
    if (editingConnection) {
      // Toujours envoyer les credentials (ils sont pré-chargés depuis le backend)
      const updated = await updateConnection(editingConnection.id, { name: data.name, credentials: data.credentials });
      setConnections(prev => prev.map(c => c.id === editingConnection.id ? updated : c));
    } else {
      const created = await createConnection(data);
      setConnections(prev => [...prev, created]);
    }
    setShowModal(false);
    setEditingConnection(null);
  };

  const getProviderIcon = (provider: string) => {
    if (provider === 'fabric') return (
      <img src="/icons/fabric/fabric_capacity.svg" alt="Fabric" style={{ width: 24, height: 24, objectFit: 'contain' }} />
    );
    if (provider === 'purview') return (
      <span style={{ fontSize: 22, lineHeight: 1 }}>🔍</span>
    );
    switch (provider) {
      case 'azure': return <FiServer className="text-blue-600" size={24} />;
      case 'aws':   return <SiAmazon className="text-orange-600" size={24} />;
      case 'gcp':   return <SiGooglecloud className="text-green-600" size={24} />;
      default:      return <FiServer size={24} />;
    }
  };

  const getProviderLabel = (provider: string) => {
    const labels: Record<string, string> = {
      azure:   'Microsoft Azure',
      aws:     'Amazon AWS',
      gcp:     'Google Cloud',
      fabric:  'Microsoft Fabric',
      purview: 'Microsoft Purview',
    };
    return labels[provider] || provider;
  };

  const getStatusBadge = (connection: ConnectionService, isTesting: boolean) => {
    if (isTesting) return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        <div className="animate-spin h-3 w-3 border-2 border-yellow-700 border-t-transparent rounded-full" />
        {t('connections.status.testing')}
      </span>
    );
    if (connection.test_status === 'success') return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <FiCheckCircle size={14} /> {t('connections.status.connected')}
      </span>
    );
    if (connection.test_status === 'failed') return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <FiXCircle size={14} /> {t('connections.status.failed')}
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        Non testé
      </span>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Jamais';
    const diffMins = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
    if (diffMins < 1) return t('connections.time.justNow');
    if (diffMins < 60) return t('connections.time.minutesAgo', { count: diffMins });
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('connections.time.hoursAgo', { count: diffHours });
    return t('connections.time.daysAgo', { count: Math.floor(diffHours / 24) });
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center min-h-[300px]">
      <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="p-8">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <FiAlertCircle className="text-red-600 shrink-0" size={20} />
        <div className="flex-1">
          <p className="font-medium text-red-800">Erreur de chargement</p>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
        <button onClick={loadConnections} className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
          Réessayer
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('connections.header.title')}</h2>
          <p className="text-gray-600 mt-1">{t('connections.header.subtitle')}</p>
        </div>
        <button
          onClick={() => { setEditingConnection(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition shadow-md"
        >
          <FiPlus size={18} /> {t('connections.buttons.newConnection')}
        </button>
      </div>

      {connections.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FiServer size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('connections.empty.title')}</h3>
          <p className="text-gray-500 mb-6">{t('connections.empty.subtitle')}</p>
          <button
            onClick={() => { setEditingConnection(null); setShowModal(true); }}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
          >
            <FiPlus size={20} /> {t('connections.buttons.addConnection')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {connections.map((connection) => (
            <div key={connection.id} className="bg-white rounded-xl border border-gray-200 hover:shadow-md transition">
              <div className={`h-2 rounded-t-xl ${!connection.is_active ? 'bg-gray-300' : connection.provider === 'azure' ? 'bg-blue-600' : connection.provider === 'aws' ? 'bg-orange-500' : connection.provider === 'fabric' ? 'bg-teal-600' : connection.provider === 'purview' ? 'bg-purple-600' : 'bg-green-500'}`} />
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    {getProviderIcon(connection.provider)}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{connection.name}</h3>
                      <p className="text-sm text-gray-500">{getProviderLabel(connection.provider)}</p>
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === connection.id ? null : connection.id); }}
                      className="p-2 hover:bg-gray-100 rounded-full transition"
                    >
                      <FiMoreVertical className="text-gray-600" />
                    </button>
                    {activeMenu === connection.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleTestConnection(connection.id)} className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                          <FiPlay size={16} /> {t('connections.menu.test')}
                        </button>
                        <button onClick={() => { setEditingConnection(connection); setShowModal(true); setActiveMenu(null); }} className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                          <FiEdit2 size={16} /> {t('connections.menu.edit')}
                        </button>
                        <button onClick={() => handleToggleConnection(connection.id)} className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                          {connection.is_active ? <FiXCircle size={16} className="text-orange-500" /> : <FiCheckCircle size={16} className="text-green-500" />}
                          {connection.is_active ? 'Désactiver' : 'Activer'}
                        </button>
                        <button onClick={() => handleDeleteConnection(connection.id)} className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600">
                          <FiTrash2 size={16} /> {t('connections.menu.delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('connections.labels.status')}</span>
                    {getStatusBadge(connection, testingId === connection.id)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('connections.labels.lastTested')}</span>
                    <span className="text-sm text-gray-900">{formatDate(connection.last_tested_at)}</span>
                  </div>
                  {connection.test_status === 'failed' && connection.test_message && (
                    <p className="text-xs text-red-500">{connection.test_message}</p>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-sm text-gray-500">Activée</span>
                    <button
                      onClick={() => handleToggleConnection(connection.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${connection.is_active ? 'bg-blue-600' : 'bg-gray-300'}`}
                      title={connection.is_active ? 'Désactiver la connexion' : 'Activer la connexion'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${connection.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ConnectionModal
          connection={editingConnection}
          onClose={() => { setShowModal(false); setEditingConnection(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ConnectionModalProps {
  connection: ConnectionService | null;
  onClose: () => void;
  onSave: (data: CreateConnectionData) => Promise<void>;
}

const ConnectionModal: React.FC<ConnectionModalProps> = ({ connection, onClose, onSave }) => {
  const { t } = useTranslation('settings');
  const [saving, setSaving] = useState(false);
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [provider, setProvider] = useState<Provider>((connection?.provider as Provider) || 'azure');
  const [name, setName] = useState(connection?.name || '');
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [showGcpGuide, setShowGcpGuide] = useState(false);
  const [awsRegions, setAwsRegions] = useState<{ code: string; label: string; group: string }[]>([]);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    fetch(`${apiUrl}/api/config/aws-regions`)
      .then(r => r.json())
      .then(data => setAwsRegions(data.regions ?? []))
      .catch(() => {}); // fallback silencieux → liste vide → select masqué
  }, []);

  // Charger les credentials existants lors de l'édition
  useEffect(() => {
    if (!connection?.id) return;
    setLoadingCreds(true);
    getConnectionWithCredentials(connection.id)
      .then(c => { if (c.credentials) setCredentials(c.credentials); })
      .catch(() => { /* credentials stay empty, user can re-enter */ })
      .finally(() => setLoadingCreds(false));
  }, [connection?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { alert(t('connections.modal.nameRequired')); return; }
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ name, provider, credentials });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Erreur lors de la sauvegarde';
      const details = err.response?.data?.details;
      setSaveError(details ? `${msg}: ${details}` : msg);
      console.error('Create/Update connection error:', err.response?.data || err);
    } finally {
      setSaving(false);
    }
  };

  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {connection ? t('connections.modal.titleEdit') : t('connections.modal.titleNew')}
          </h2>
        </div>
        {loadingCreds && (
          <div className="flex items-center justify-center py-6 gap-2 text-blue-600 text-sm">
            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
            Chargement des identifiants...
          </div>
        )}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('connections.modal.nameLabel')}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={ic} placeholder={t('connections.modal.namePlaceholder')} required />
          </div>

          {!connection && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">{t('connections.modal.providerLabel')}</label>
              <div className="grid grid-cols-5 gap-3">
                {(['azure', 'aws', 'gcp', 'fabric', 'purview'] as Provider[]).map((p) => {
                  const activeColor = p === 'fabric' ? 'border-teal-500 bg-teal-50' : p === 'purview' ? 'border-purple-500 bg-purple-50' : 'border-blue-500 bg-blue-50';
                  return (
                    <button key={p} type="button" onClick={() => { setProvider(p); setCredentials({}); }}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition ${provider === p ? activeColor : 'border-gray-200 hover:border-gray-300'}`}>
                      {p === 'azure'   && <FiServer size={24} className="text-blue-600" />}
                      {p === 'aws'     && <SiAmazon size={24} className="text-orange-600" />}
                      {p === 'gcp'     && <SiGooglecloud size={24} className="text-red-600" />}
                      {p === 'fabric'  && <img src="/icons/fabric/fabric_capacity.svg" alt="Fabric" style={{ width: 24, height: 24, objectFit: 'contain' }} />}
                      {p === 'purview' && <span style={{ fontSize: 24 }}>🔍</span>}
                      <span className="text-xs font-medium text-gray-900">
                        {p === 'azure' ? 'Azure' : p === 'aws' ? 'AWS' : p === 'gcp' ? 'GCP' : p === 'fabric' ? 'Fabric' : 'Purview'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {provider === 'azure' && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-gray-900">{t('connections.modal.azureTitle')}</h3>
              {[
                { key: 'tenantId', label: t('connections.modal.tenantId'), ph: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', type: 'text' },
                { key: 'clientId', label: t('connections.modal.clientId'), ph: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', type: 'text' },
                { key: 'subscriptionId', label: t('connections.modal.subscriptionId'), ph: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', type: 'text' },
                { key: 'clientSecret', label: t('connections.modal.clientSecret'), ph: '•••••••••••••••••', type: 'password' },
              ].map(({ key, label, ph, type }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type={type} value={credentials[key] || ''} onChange={(e) => setCredentials({ ...credentials, [key]: e.target.value })} className={ic} placeholder={ph} />
                </div>
              ))}
            </div>
          )}

          {provider === 'aws' && (
            <div className="space-y-4 p-4 bg-orange-50 rounded-lg">
              <h3 className="font-semibold text-gray-900">{t('connections.modal.awsTitle')}</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('connections.modal.accessKeyId')}</label>
                <input type="text" value={credentials.accessKeyId || ''} onChange={(e) => setCredentials({ ...credentials, accessKeyId: e.target.value })} className={ic} placeholder="AKIAIOSFODNN7EXAMPLE" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('connections.modal.secretAccessKey')}</label>
                <input type="password" value={credentials.secretAccessKey || ''} onChange={(e) => setCredentials({ ...credentials, secretAccessKey: e.target.value })} className={ic} placeholder="•••••••••••••••••" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('connections.modal.region')}</label>
                <select value={credentials.region || 'eu-west-3'} onChange={(e) => setCredentials({ ...credentials, region: e.target.value })} className={ic}>
                  {Object.entries(
                    awsRegions.reduce((acc, r) => {
                      if (!acc[r.group]) acc[r.group] = [];
                      acc[r.group].push(r);
                      return acc;
                    }, {} as Record<string, typeof awsRegions>)
                  ).map(([group, regions]) => (
                    <optgroup key={group} label={group}>
                      {regions.map(r => (
                        <option key={r.code} value={r.code}>{r.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
          )}

          {provider === 'gcp' && (
            <div className="space-y-4 p-4 bg-green-50 rounded-lg">
              <h3 className="font-semibold text-gray-900">{t('connections.modal.gcpTitle')}</h3>

              {/* Guide de configuration */}
              <div className="bg-white border border-green-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowGcpGuide(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 transition"
                >
                  <span>Comment configurer une connexion GCP ?</span>
                  {showGcpGuide ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                </button>
                {showGcpGuide && (
                  <div className="px-3 pb-3 text-xs text-gray-700 space-y-2 border-t border-green-100 pt-3">
                    <p className="text-gray-500">GenIaC gère le cycle de vie complet du projet GCP (création, mise à jour). Si le projet existe déjà dans le state Terraform, il ne sera pas recréé.</p>
                    <ol className="list-decimal list-inside space-y-1.5 text-gray-600">
                      <li>Dans la console GCP, sélectionne un <strong>projet existant</strong> qui hébergera le Service Account (ex: My First Project)</li>
                      <li>IAM &amp; Admin → Comptes de service → <strong>Créer</strong> → rôle <strong>Editor</strong> sur ce projet → Clés → JSON → télécharger</li>
                      <li>Dans IAM au niveau de l'<strong>organisation</strong>, accorde à ce SA les rôles :
                        <span className="block ml-4 mt-0.5 font-mono bg-green-50 px-1 rounded">Créateur de projets</span>
                        <span className="block ml-4 font-mono bg-green-50 px-1 rounded">Utilisateur de compte de facturation</span>
                      </li>
                      <li>Dans <strong>Facturation → Gestion de compte</strong>, ouvre ton compte de facturation → <strong>Ajouter un principal</strong> → ajoute le SA avec le rôle <strong>Utilisateur de compte de facturation</strong></li>
                      <li><strong>Organization ID</strong> : IAM &amp; Admin → Paramètres → "ID de l'organisation"</li>
                      <li><strong>Billing Account ID</strong> : Facturation → Gérer les comptes → colonne "ID du compte"</li>
                      <li><strong>Project ID</strong> ci-dessous = ID du projet <em>hôte</em> du Service Account (pas le projet à créer)</li>
                    </ol>
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <p className="font-semibold text-yellow-800 mb-1">APIs à activer dans le projet hôte (ex: Proj-Infra-Admin) :</p>
                      <ul className="list-disc list-inside space-y-0.5 text-yellow-700">
                        <li><span className="font-mono">cloudresourcemanager.googleapis.com</span> — Cloud Resource Manager API</li>
                        <li><span className="font-mono">cloudbilling.googleapis.com</span> — Cloud Billing API</li>
                        <li><span className="font-mono">serviceusage.googleapis.com</span> — Service Usage API</li>
                      </ul>
                      <p className="mt-1 text-yellow-600">Console GCP → APIs &amp; Services → Bibliothèque → activer chacune dans le projet hôte</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('connections.modal.projectId')} <span className="text-gray-400 font-normal">(projet hôte du Service Account)</span></label>
                <input type="text" value={credentials.projectId || ''} onChange={(e) => setCredentials({ ...credentials, projectId: e.target.value })} className={ic} placeholder="my-host-project-123456" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('connections.modal.serviceAccountKey')}</label>
                <textarea value={credentials.serviceAccountKey || ''} onChange={(e) => setCredentials({ ...credentials, serviceAccountKey: e.target.value })} className={`${ic} font-mono text-xs`} rows={4} placeholder='{"type": "service_account", ...}' />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization ID</label>
                <input type="text" value={credentials.orgId || ''} onChange={(e) => setCredentials({ ...credentials, orgId: e.target.value })} className={ic} placeholder="560177901394" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Account ID</label>
                <input type="text" value={credentials.billingAccount || ''} onChange={(e) => setCredentials({ ...credentials, billingAccount: e.target.value })} className={ic} placeholder="01B5CB-6C99A4-8E300A" />
              </div>
            </div>
          )}

          {provider === 'purview' && (
            <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span style={{ fontSize: 20 }}>🔍</span>
                <h3 className="font-semibold text-gray-900">Microsoft Purview — Service Principal</h3>
              </div>
              <div className="bg-white border border-purple-200 rounded-lg p-3 text-xs text-gray-600 space-y-1">
                <p className="font-medium text-purple-700">Comment configurer ?</p>
                <p>1. Créez une App Registration dans Entra ID</p>
                <p>2. Attribuez le rôle <strong>Purview Data Reader</strong> sur le compte Purview</p>
                <p>3. Générez un Client Secret dans l'App Registration</p>
                <p>4. L'endpoint se trouve dans le portail Azure → Purview → Vue d'ensemble → Endpoint</p>
              </div>
              {[
                { key: 'tenantId',     label: 'Tenant ID',     ph: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', type: 'text' },
                { key: 'clientId',     label: 'Client ID (App Registration)', ph: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', type: 'text' },
                { key: 'clientSecret', label: 'Client Secret', ph: '•••••••••••••••••', type: 'password' },
              ].map(({ key, label, ph, type }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type={type} value={credentials[key] || ''} onChange={(e) => setCredentials({ ...credentials, [key]: e.target.value })} className={ic} placeholder={ph} />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint Purview</label>
                <input type="text" value={credentials.endpoint || ''} onChange={(e) => setCredentials({ ...credentials, endpoint: e.target.value })} className={ic} placeholder="https://moncompte.purview.azure.com" />
              </div>
              <div className="bg-purple-100 rounded p-2 text-xs text-purple-800">
                Le SP doit avoir le rôle <strong>Purview Data Reader</strong> pour lire les assets et classifications, ou <strong>Purview Data Curator</strong> pour déclencher des scans.
              </div>
            </div>
          )}

          {provider === 'fabric' && (
            <div className="space-y-4 p-4 bg-teal-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <img src="/icons/fabric/fabric_capacity.svg" alt="Fabric" style={{ width: 20, height: 20 }} />
                <h3 className="font-semibold text-gray-900">Microsoft Fabric — Service Principal</h3>
              </div>
              <div className="bg-white border border-teal-200 rounded-lg p-3 text-xs text-gray-600 space-y-2">
                <p className="font-medium text-teal-700">Comment configurer le Service Principal ?</p>
                <div className="space-y-1">
                  <p className="font-medium text-gray-700">1. Créer l'App Registration</p>
                  <p className="pl-2">Entra ID → App Registrations → New registration</p>
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-gray-700">2. Ajouter les permissions API (Application)</p>
                  <p className="pl-2">App Registration → API permissions → Add :</p>
                  <ul className="pl-4 space-y-0.5">
                    <li>• <strong>Microsoft Graph</strong> → <code>User.Read.All</code> (Application) — RBAC workspace</li>
                    <li>• <strong>Power BI Service</strong> → <code>Tenant.Read.All</code> (Application) — monitoring</li>
                  </ul>
                  <p className="pl-2 text-orange-600">→ Cliquer "Accorder un consentement administrateur"</p>
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-gray-700">3. Rôles Fabric requis</p>
                  <ul className="pl-4 space-y-0.5">
                    <li>• Portail Fabric → Admin Portal → <strong>Paramètres du client</strong> → activer <strong>"Les principaux de service peuvent utiliser les API Fabric"</strong></li>
                    <li>• Portail Fabric → Admin Portal → <strong>Paramètres du client</strong> → activer <strong>"Les principaux de service peuvent accéder aux API d'administration en lecture seule"</strong></li>
                    <li>• Portail Fabric → Admin Portal → <strong>Paramètres du client</strong> → <strong>"Créer des espaces de travail"</strong> → inclure le SP ou son groupe de sécurité</li>
                    <li>• Entra ID → rôle <strong>Fabric Administrator</strong> sur le SP (ou via groupe)</li>
                  </ul>
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-gray-700">4. Générer un Client Secret</p>
                  <p className="pl-2">App Registration → Certificates & secrets → New client secret</p>
                </div>
              </div>
              {[
                { key: 'tenantId',     label: 'Tenant ID',     ph: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', type: 'text' },
                { key: 'clientId',     label: 'Client ID (App Registration)', ph: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', type: 'text' },
                { key: 'clientSecret', label: 'Client Secret', ph: '•••••••••••••••••', type: 'password' },
              ].map(({ key, label, ph, type }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type={type} value={credentials[key] || ''} onChange={(e) => setCredentials({ ...credentials, [key]: e.target.value })} className={ic} placeholder={ph} />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fabric Capacity ID <span className="text-gray-400 font-normal">(optionnel — requis pour Trial capacity)</span>
                </label>
                <input
                  type="text"
                  value={credentials.capacityId || ''}
                  onChange={(e) => setCredentials({ ...credentials, capacityId: e.target.value })}
                  className={ic}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Portail Fabric → Settings → Capacity → ID de capacité. Requis pour attacher les workspaces à une Trial capacity (sinon Lakehouse/Warehouse non disponibles).
                </p>
              </div>
              <div className="bg-teal-100 rounded p-2 text-xs text-teal-800">
                Ces credentials génèrent les variables Terraform : <code>fabric_tenant_id</code>, <code>fabric_client_id</code>, <code>fabric_client_secret</code>, et optionnellement <code>fabric_capacity_id</code>.
              </div>
            </div>
          )}

          {saveError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <FiAlertCircle size={16} /> {saveError}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50">
              {t('connections.modal.cancel')}
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
              {connection ? t('connections.modal.save') : t('connections.modal.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConnectionsPage;
